import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import Can from "./Can.js";
import { MdPalette } from "react-icons/md";
import AkanthasOwlIcon from "./icons/AkanthasOwlIcon";
import Dropdown from "../ui/components/Dropdown";
import {
  updateUserCorporation,
  updateUserLanguage,
} from "../services/users.js";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserLanguage } from "../redux/languageSlice.js";
import { languageData } from "../shared/utils/utils.js";
import { useTranslation } from "react-i18next";
import { FaBuilding, FaLock } from "react-icons/fa";
import Cookies from "js-cookie";
import { ReactComponent as IconUsers } from "../static/assets/header-users.svg";
import { ReactComponent as IconConfrontation } from "../static/assets/header-confrontation.svg";
import { ReactComponent as IconDocuments } from "../static/assets/header-documents.svg";
import { ReactComponent as IconHelp } from "../static/assets/header-help.svg";
import { ReactComponent as IconLogout } from "../static/assets/header-logout.svg";
import { ReactComponent as IconApi } from "../static/assets/header-api.svg";

const storybookUrl = process.env.REACT_APP_STORYBOOK_URL;
const corporationDropdownIcon = <FaBuilding size={15} aria-hidden />;

const HeaderMenuItem = ({ icon: Icon, children, onClick, href }) => {
  const content = (
    <>
      <span className="header-user-dialog-icon" aria-hidden>
        <Icon />
      </span>
      {children}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="header-user-dialog-item"
        onClick={onClick}
      >
        {content}
      </a>
    );
  }

  return (
    <button type="button" className="header-user-dialog-item" onClick={onClick}>
      {content}
    </button>
  );
};

const Header = ({
  saveUser,
  setCookies,
  userData,
  licenseExpired,
  corporation,
}) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeCorporation, setActiveCorporation] = useState(null);
  const [multiCorporations, setMultiCorporations] = useState([]);
  const language = useSelector((state) => state.language.data);
  const dispatch = useDispatch();
  const userRef = useRef(null);
  const history = useHistory();
  const isAkanthasAccount = (Cookies.get("userEmail") || "").includes(
    "@akanthas.com",
  );
  const viewMode = Cookies.get("viewMode") || "client";
  const showStorybookLink =
    Boolean(storybookUrl) && isAkanthasAccount && viewMode === "admin";
  const displayName = [userData?.firstName, userData?.lastName]
    .filter(Boolean)
    .join(" ");

  function close() {
    setIsOpen(false);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userRef.current && !userRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (userData && userData.corporation) {
      if (
        userData?.multiCorporations &&
        userData.multiCorporations.length > 1
      ) {
        const selectedCorporation = userData.multiCorporations.filter(
          (item) => item._id === userData.corporation,
        )[0];
        setActiveCorporation(selectedCorporation);
        setMultiCorporations(
          userData.multiCorporations.map((item) => ({
            value: item._id,
            label: item.corporationName,
            icon: corporationDropdownIcon,
          })),
        );
      } else {
        setActiveCorporation(corporation);
        setMultiCorporations([]);
      }
    }
  }, [userData, corporation]);

  const changeCorporation = async (value) => {
    if (!userData?._id) return;
    await updateUserCorporation({ corporation: value }, userData._id)
      .then(async (response) => {
        await saveUser(response.data);
        window.location.reload(true);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changeLanguage = async (languageValue) => {
    const item = languageData.find(
      (entry) => String(entry.value) === String(languageValue),
    );
    if (!item || !userData?._id) return;

    await updateUserLanguage({ language: item.value }, userData._id)
      .then(() => {
        localStorage.setItem("language", item.value);
        dispatch(fetchUserLanguage(userData._id));
        i18n.changeLanguage(item.value);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const goTo = (path) => {
    close();
    history.push(path);
  };

  const handleLogout = async () => {
    close();
    await setCookies?.(null, null, null);
    await saveUser?.(null);
    localStorage.clear();
    sessionStorage.clear();
    history.push("/login");
  };

  return (
    <header className="header bg-white border-b border-[#D3D3D3]">
      <section className="header-wrapper flex flex-row items-center gap-3">
        <article className="z-30">
          {licenseExpired === true && (
            <FaLock
              style={{ marginRight: 15, color: "#0F265C", fontSize: 18 }}
            />
          )}
        </article>
        {multiCorporations.length > 1 ? (
          <article className="z-30">
            <Dropdown
              options={multiCorporations}
              value={activeCorporation?._id || userData?.corporation}
              onChange={changeCorporation}
              aria-label={t("Company")}
            />
          </article>
        ) : null}

        <article>
          <Dropdown
            options={languageData.map(({ value, label, flag }) => ({
              value,
              label,
              flag,
            }))}
            value={language?.value || i18n.language || "fr"}
            onChange={changeLanguage}
            aria-label={t("Language")}
          />
        </article>

        <article className="header-user" ref={userRef}>
          <button
            type="button"
            className="header-button"
            aria-label={t("Account")}
            aria-expanded={isOpen}
            aria-haspopup="true"
            onClick={() => setIsOpen((open) => !open)}
          >
            <AkanthasOwlIcon className="logoHeader" size={27} />
          </button>
          <div
            className={`header-user-dialog ${isOpen ? "visible" : ""}`}
            role="menu"
            aria-label={t("Account")}
          >
            <div className="header-user-dialog-head">
              <p className="header-user-dialog-name">{displayName}</p>
              <span className="header-user-dialog-badge">Akanthas</span>
            </div>
            <div className="header-user-dialog-body">
              <Can
                role={userData?.role}
                perform="navbar:access"
                yes={() => (
                  <HeaderMenuItem
                    icon={IconUsers}
                    onClick={() => goTo("/access")}
                  >
                    {t("Access")}
                  </HeaderMenuItem>
                )}
              />
              <HeaderMenuItem
                icon={IconConfrontation}
                onClick={() => goTo("/confrontation")}
              >
                {t("Confrontations")}
              </HeaderMenuItem>
              <HeaderMenuItem
                icon={IconDocuments}
                onClick={() => goTo("/documents")}
              >
                {t("My Akanthas Documents")}
              </HeaderMenuItem>
              <HeaderMenuItem icon={IconApi} onClick={() => goTo("/api")}>
                {t("corp_api_nav_label")}
              </HeaderMenuItem>
              <HeaderMenuItem icon={IconHelp} onClick={() => goTo("/help")}>
                {t("Help center")}
              </HeaderMenuItem>
              {showStorybookLink && (
                <HeaderMenuItem
                  icon={MdPalette}
                  href={storybookUrl}
                  onClick={close}
                >
                  {t("Design system")}
                </HeaderMenuItem>
              )}
              <div className="header-user-dialog-separator" />
              <HeaderMenuItem icon={IconLogout} onClick={handleLogout}>
                {t("Logout")}
              </HeaderMenuItem>
            </div>
          </div>
        </article>
      </section>
    </header>
  );
};

export default Header;
